import React, { useCallback } from "react";
import ModalWrapper from "..";
import { Button } from "../../button";
import toast from "react-hot-toast";
import * as Yup from "yup";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import TextInput from "../../inputs/TextInput";
import { useTranslation } from "react-i18next";
import { useRejectAppointment } from "../../../hooks/visitor/mutation";
import useAuthStore from "../../../store/Auth";
import { STATUS_IDS } from "../../../constants/common";
import { APP_ROUTES } from "../../../routes";
import { useNavigate } from "react-router-dom";
import i18n from "../../../i18n";

interface RejectAppointmentModalProps {
  appointment: any;
  isOpen: boolean;
  onOpenChange: () => void;
}

const RejectAppointmentModalSchema = Yup.object({
  comments: Yup.string().required(
    i18n.language === "en" ? "Comments is required." : "التعليقات مطلوبة."
  ),
});

interface RejectAppointmentModalData {
  comments: string;
}

const RejectAppointmentModal = ({
  appointment,
  isOpen,
  onOpenChange,
}: RejectAppointmentModalProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useFormWithSchema(RejectAppointmentModalSchema);

  const { t } = useTranslation();

  const {
    mutateAsync: RejectAppointment,
    isPending: isRejectAppointmentLoading,
  } = useRejectAppointment();

  const navigate = useNavigate();

  const user = useAuthStore((state) => state.user);

  const handleRejectAppointment = useCallback(
    async (data: RejectAppointmentModalData) => {
      try {
        const body = {
          N_LocationID: appointment?.n_LocationID,
          N_AppointmentID: appointment?.n_AppointmentID,
          N_UserID: user?.user_id,
          N_RoleID: user?.n_RoleID,
          N_ApprovalStatus: STATUS_IDS.REJECTED,
          N_BuildingID: appointment?.n_BuildingID,
          N_FloorID: 0,
          N_MeetingRoomID: appointment?.n_MeetingRoomID,
          N_DepartmentID: appointment?.n_DepartmentID,
          N_VisitType: appointment?.n_VisitType,
          Purpose: appointment?.purpose,
          Dt_AppointmentStartTime: appointment?.dt_AppointmentStartTime,
          Dt_AppointmentEndTime: appointment?.dt_AppointmentEndTime,
          N_HostID: appointment?.n_HostID,
          S_HostComments: data.comments,
        };

        await RejectAppointment(body);
        toast.success(t("appointmentRejected"));

        setTimeout(() => {
          navigate(APP_ROUTES.APPROVE_VISIT_REQUEST);
        }, 700);
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      } finally {
        onOpenChange();
      }
    },
    [
      appointment?.n_LocationID,
      appointment?.n_AppointmentID,
      appointment?.n_BuildingID,
      appointment?.n_MeetingRoomID,
      appointment?.n_DepartmentID,
      appointment?.n_VisitType,
      appointment?.purpose,
      appointment?.dt_AppointmentStartTime,
      appointment?.dt_AppointmentEndTime,
      appointment?.n_HostID,
      user?.user_id,
      user?.n_RoleID,
      RejectAppointment,
      t,
      navigate,
      onOpenChange,
    ]
  );

  return (
    <ModalWrapper
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      contentStyles="md:!max-w-2xl"
    >
      <form
        onSubmit={handleSubmit(handleRejectAppointment)}
        className="flex flex-col gap-5 justify-center items-center w-full"
      >
        <p className="text-lg font-medium">{t("confirmRejection")}</p>

        <div className="w-3/4">
          <Controller
            name="comments"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("comments")}
                onChange={onChange}
                error={errors?.[name]?.message}
                required
                value={value}
              />
            )}
          />
        </div>

        <div className="flex gap-2 items-center">
          <Button
            isLoading={isRejectAppointmentLoading}
            className=" bg-red-600 hover:bg-red-700"
          >
            {t("reject")}
          </Button>

          <Button
            onClick={onOpenChange}
            className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
          >
            {t("close")}
          </Button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default React.memo(RejectAppointmentModal);
